import { Link } from "react-router-dom";
import { ShieldCheck, BadgeCheck, UserCheck, MapPin, UtensilsCrossed, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Motion";

const promises = [
  { n: "01", icon: ShieldCheck, title: "Fully Insured & Bonded", desc: "Every job is covered by full liability insurance and bonding — your property and peace of mind are protected." },
  { n: "02", icon: UserCheck, title: "Background-Checked Staff", desc: "Every cleaner is vetted, trained and background-checked. These are people you can trust with your keys." },
  { n: "03", icon: BadgeCheck, title: "Satisfaction Guaranteed", desc: "If any area isn't right, we come back and re-clean it free. We don't leave until it meets our standard." },
];

export default function About() {
  return (
    <div>
      <section className="pt-32 md:pt-40 pb-16 bg-secondary grain relative">
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-tide">About Bright &amp; Tide</span>
            <h1 className="mt-4 font-heading font-extrabold tracking-tighter text-5xl md:text-6xl text-ink leading-[1.05]">
              A cleaning company you'd trust with your keys.
            </h1>
            <p className="mt-6 text-ink/65 text-lg leading-relaxed">
              Bright &amp; Tide Cleaning is a GTA-based team built on a simple promise: spotless spaces,
              healthier places and better impressions — delivered by people you can rely on.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="clip-arch overflow-hidden ring-1 ring-border shadow-[0_30px_80px_-30px_rgba(4,27,36,0.4)]">
              <img src="https://images.pexels.com/photos/9462636/pexels-photo-9462636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Bright & Tide cleaning team" className="w-full h-[420px] md:h-[520px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <Reveal>
          <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-tide">Our Promise</span>
          <h2 className="mt-4 font-heading font-bold tracking-tight text-4xl md:text-5xl text-ink max-w-2xl">Trust, built into every clean.</h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {promises.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.1}>
              <div className="h-full rounded-sm border border-border bg-white p-8">
                <span className="font-mono text-4xl font-bold text-tide/15">{p.n}</span>
                <div className="mt-3 h-12 w-12 rounded-sm bg-tide/10 text-tide flex items-center justify-center"><p.icon size={24} /></div>
                <h3 className="mt-5 font-heading font-bold text-xl text-ink">{p.title}</h3>
                <p className="mt-2 text-ink/60 leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ink text-white grain relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28 grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
          <Reveal>
            <div className="h-14 w-14 rounded-sm bg-tide/15 text-tide-bright flex items-center justify-center"><UtensilsCrossed size={26} /></div>
            <h2 className="mt-6 font-heading font-bold tracking-tight text-4xl md:text-5xl">Founded in the restaurant trenches.</h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="space-y-5 text-white/75 text-lg leading-relaxed">
              <p>
                Our founder spent years in the restaurant industry, where cleanliness isn't optional — it's the
                difference between passing a health inspection and closing your doors. That standard shaped everything we do.
              </p>
              <p>
                We brought that back-of-house discipline to every space we clean, whether it's a bustling kitchen,
                a corporate office, a school or a family home. Rooted in the GTA, we clean the neighbourhoods we live in.
              </p>
              <div className="flex items-center gap-2 text-tide-bright font-semibold pt-2">
                <MapPin size={18} /> Proudly serving the Greater Toronto Area
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-tide">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20 flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight text-white">Experience the Bright &amp; Tide standard.</h2>
          <Link to="/book" className="inline-flex items-center gap-2 rounded-sm bg-white text-ink font-bold px-8 py-4 transition-transform hover:-translate-y-1">
            Book Now <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
